import { useEffect, useRef, useState } from 'react';

import { Col, Flex, Row } from 'antd';
import { Button, Switch } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { DeviceValue } from '../services/cache/idb';
import { Mutex, MutexInterface, withTimeout } from 'async-mutex';
import { CSM_PROTOCOL_COMMAND_TYPE, CSM_PROTOCOL_EVENT_TYPE } from '../components/serial/csm';
import { v4 as uuidv4 } from 'uuid';
import { loadHumidityCalibrant, loadSpotsGrid1D } from '../services/cache/localStorage';
import { parseBiosensorsSignalMessagePayload, shouldCompute, updateKineticSeries } from '../components/analysis/compute';
import { computeSignatureFromSignal, DEFAULT_SIGNATURE_PROCESSING_PARAMS } from '../components/analysis/signature';
import { processMziData } from '../components/analysis/mzi';
import { SignatureWithSpotgrid } from '../types/types';
import { QuestioningState } from './OdorIdentificationPage/NoaOdorIdentificationContainer';
import { Paper } from '../components/common/common';
import { IntensityGauge } from '../components/widgets/Gauges/IntensityGauge';
import { HumidityGauge } from '../components/widgets/Gauges/HumidityGauge';
import { useMessageContext } from '../state/context/MessageContext';

type BleOdorIdentificationProps = {};

const Monitoring: React.FC<BleOdorIdentificationProps> = () => {
  const { csmMessages, csmFwVersion, consumeCSMMessage, clearCSMMessages, addCSMCommand, hihValues } = useMessageContext();

  // * gauge tooling
  const MIN_VALUE = -7; // Define minimum value for the gauge
  const MAX_VALUE = 7; // Define maximum value for the gauge
  const mapValueToPercent = (value: number): number => {
    // Map the value (-100 to 100) to a percentage (0 to 100)
    return ((value - MIN_VALUE) / (MAX_VALUE - MIN_VALUE)) * 100;
  };
  // *

  // * MZI and timestamps related useRefs
  const firstMZIsRef = useRef<number[] | null>(null);
  const previousMZIsRef = useRef<number[] | null>(null);
  const signalEnvelopeAvgRef = useRef<number>(0);
  const rawMZISeriesRef = useRef<number[][]>([]);
  const averageMZISeriesRef = useRef<number[]>([]);

  const rawTimestampSeriesRef = useRef<number[]>([]);
  const lastDecimationTickRef = useRef<number>(0);
  const noizeLevelRef = useRef<number>(0);

  // * device related useState
  const [deviceValue, setDeviceValue] = useState<DeviceValue | null>(null);
  const [currentSpotsgrid1d, setCurrentSpotsgrid1d] = useState<number[] | null>(null);

  // * humidity compensation
  const [humidityCalibrant, setHumidityCalibrant] = useState<SignatureWithSpotgrid | undefined>(undefined);
  const humidityBaselineRef = useRef<number | null>(null);
  const [humidityCompensationEnabled, sethumidityCompensationEnabled] = useState<boolean>(true);
  const humidityDisplayed = useRef<number>(0);
  const temperatureDisplayed = useRef<number>(0);

  // * Algo tooling  for later
  const NOISE_MULTIPLYING_FACTOR = 30;
  const isOdorPresentRef = useRef<boolean>(false);
  const isMZIcomputingrequested = useRef<boolean>(false);
  const odorPresenceThresholdLevelRef = useRef<number>(0);
  const [questioningState, setQuestioningState] = useState<QuestioningState>(QuestioningState.BaselineRecording);
  const questionState = useRef<QuestioningState>(QuestioningState.BaselineRecording);
  const maxOdorPresentValue = useRef<number>(0);
  const decimatedMZISeriesCorrected = useRef<number[][]>([]);
  const [questionningSignature, setQuestionningSignature] = useState<number[] | null>(null);
  const [questionningSpotsgrid1d, setQuestionningSpotsgrid1d] = useState<number[] | null>(null);

  // *
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // mutex
  const messageQueueMutexRef = useRef<MutexInterface>(withTimeout(new Mutex(), 300));

  // start sampling when mounting the component
  useEffect(() => {
    clearCSMMessages();
    addCSMCommand({
      id: uuidv4().toString(),
      message: {
        CmdType: CSM_PROTOCOL_COMMAND_TYPE.StartSampling,
      },
    });
    setIsLoading(false);
    return () => {
      addCSMCommand({
        id: uuidv4().toString(),
        message: {
          CmdType: CSM_PROTOCOL_COMMAND_TYPE.StopSampling,
        },
      });
      setIsLoading(true);
    };
  }, []);

  // load humidity calibrant
  useEffect(() => {
    const hCalibrant = loadHumidityCalibrant();
    setHumidityCalibrant(hCalibrant);
    console.log('humidityCalibrant is', hCalibrant);
  }, []);

  // load spotgrid from local storage
  useEffect(() => {
    let _spotsgrid1d = loadSpotsGrid1D();
    if (!_spotsgrid1d) {
      console.log('sense page: spotsgrid1d is empty');
      return;
    }
    setCurrentSpotsgrid1d(_spotsgrid1d);
  }, []);

  // watch csm messages
  useEffect(() => {
    if (csmMessages.length === 0 || messageQueueMutexRef.current.isLocked()) {
      return;
    }
    messageQueueMutexRef.current
      .acquire()
      .then((release) => {
        let nFramesProcessed = 0;
        csmMessages.forEach((message) => {
          if (message.message.Type !== CSM_PROTOCOL_EVENT_TYPE.BiosensorsSignalEvent) {
            console.log('Non-biosensor event:', message.message);
            consumeCSMMessage(message.id);
            return;
          } else {
            nFramesProcessed++;
            consumeCSMMessage(message.id);
            // Parse message payload
            let mzis = parseBiosensorsSignalMessagePayload(message, csmFwVersion);
            if (!mzis) return;

            // set Baseline delta H for future use
            if (humidityBaselineRef.current === null) {
              console.log('Changing humidity baseline reference as ', hihValues.humidity);
              humidityBaselineRef.current = hihValues.humidity;
            }
            // set ref
            if (firstMZIsRef.current === null) {
              firstMZIsRef.current = [...mzis];
            }
            rawMZISeriesRef.current.push(mzis);
            isMZIcomputingrequested.current = shouldCompute(message.ts, lastDecimationTickRef); // to prevent frequent rendering
            let [averageMZI, correctedMZI] = processMziData(rawMZISeriesRef.current, firstMZIsRef.current, currentSpotsgrid1d, humidityCompensationEnabled, humidityCalibrant, humidityBaselineRef.current, hihValues) || [0, []];
            updateKineticSeries(averageMZI, correctedMZI, averageMZISeriesRef, decimatedMZISeriesCorrected);
            //console.log('kinetic average is', averageMZISeriesRef)
            if (isMZIcomputingrequested.current) {
              // console.log('averageMZI...', averageMZI, ' humidity correction is', humidityCompensationEnabled, 'lastDecimationTickRef.current is', lastDecimationTickRef.current)

              // Update displayed values
              signalEnvelopeAvgRef.current = averageMZI; // for display in the component
              console.log(signalEnvelopeAvgRef.current);
              humidityDisplayed.current = hihValues.humidity;
              temperatureDisplayed.current = hihValues.temperature;
              //updateOdorPresenceAndQuestioningState(mode, averageMZI, averageMZISeriesRef.current, decimatedMZISeriesCorrected.current, isOdorPresentRef, questionState, maxOdorPresentValue, odorPresenceThresholdLevelRef)
            }
            //console.log('averageMZISeriesRef...', averageMZISeriesRef.current, '.. humidity correction is', humidityCompensationEnabled)
            // for later
            if (questionState.current === QuestioningState.OdorAnalysis) {
              console.log('computing signature...');
              constructSignatureAndRecognize();
            }
          }
        });
        rawMZISeriesRef.current = []; // do we need this reset in monitoring mode ?
        // Release the mutex after processing
        release();
      })
      .catch((e) => {
        console.error('Mutex acquisition failed', e);
        messageQueueMutexRef.current.cancel();
        messageQueueMutexRef.current.release();
      });

    return () => {
      messageQueueMutexRef.current.cancel();
      messageQueueMutexRef.current.release();
    };
  }, [csmMessages]);

  useEffect(() => {
    const constructDeviceValue = async () => {
      let commonName = 'Neose BLE';
      if (commonName === undefined || commonName === null) {
        commonName = '';
      }
      let shellSerial = '';
      let coreSensorSerial = '';
      let fwVersion = '';
      let hwVersion = '';
      let cameraExposure = 0;

      let spotsgrid = currentSpotsgrid1d;
      if (spotsgrid === undefined || spotsgrid === null) {
        throw new Error('spotsgrid is undefined');
      }

      let _deviceValue = {
        commonName,
        shellSerial,
        coreSensorSerial,
        fwVersion,
        hwVersion,
        cameraExposure,
        spotsgrid,
      };
      console.log('sense page: constructed device value', _deviceValue);
      return _deviceValue;
    };
    constructDeviceValue()
      .then((_deviceValue) => {
        setDeviceValue(_deviceValue);
      })
      .catch((e: any) => {
        console.log('sense page: could not construct device', e);
      });
  }, [currentSpotsgrid1d]);

  // reset MZI for display centered around 0
  const onClickReset = () => {
    // previousMZIsRef.current = null is to avoid synthetic phase jumps when signal drops from some value > pi/2 to zero
    previousMZIsRef.current = null;
    firstMZIsRef.current = null;
    humidityBaselineRef.current = null;
    averageMZISeriesRef.current = [];
  };

  // for later
  const constructSignatureAndRecognize = () => {
    if (!currentSpotsgrid1d) {
      console.log('sense page: spotsgrid is empty');
      return;
    }

    // configure preprocessing Pipeline based on model type
    let processingParams = DEFAULT_SIGNATURE_PROCESSING_PARAMS;
    let sectionMZIs = decimatedMZISeriesCorrected.current;

    // signature with no baseline substraction, simple analyte mean
    let [processedSignature, processedSpotgrid1d] = computeSignatureFromSignal(sectionMZIs, null, null, currentSpotsgrid1d, processingParams);
    setQuestionningSignature(processedSignature);
    setQuestionningSpotsgrid1d(processedSpotgrid1d);
  };

  return (
    <Paper>
      <Row align="top" gutter={5} style={{ marginTop: 50, marginBottom: 50 }}>
        <Col span={24} md={12}>
          <Row justify="end">
            <Button type="primary" icon={<ReloadOutlined />} onClick={onClickReset} />
          </Row>
          <IntensityGauge MZIvalue={parseFloat(signalEnvelopeAvgRef.current.toFixed(1))} hihValues={{ humidity: humidityDisplayed.current, temperature: temperatureDisplayed.current }} />
          <Flex justify="center" align="middle" gap="small" style={{ textAlign: 'center' }}>
            <p style={{ marginBottom: 0, fontSize: 16 }}>Humidity correction enabled</p>
            <Switch onChange={sethumidityCompensationEnabled} defaultChecked={humidityCompensationEnabled} />
          </Flex>
        </Col>
        <Col span={24} md={12} style={{ marginTop: 32 }}>
          <HumidityGauge humidityValue={parseFloat(humidityDisplayed.current.toFixed(1))} />
        </Col>
      </Row>
    </Paper>
  );
};

export default Monitoring;
